import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Compass, ArrowRight, ArrowLeft, X, CheckCircle2, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary";
const primary = "inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60";
const outline = "inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground hover:bg-muted";

const CATEGORIES = ["Revenue", "Growth", "Customers", "Marketing", "Product", "Sales", "Operations", "Team", "Partnerships", "Expansion", "Productivity", "Other"];

const emptyDraft = () => ({
  name: "", category: "Revenue", strategic_role: "primary",
  baseline: "", target: "", target_unit: "", target_date: "",
  priority: "high", why_matters: "", biggest_obstacle: "",
});

/**
 * StrategicGoalWizard
 * Props:
 *   open          — controls visibility
 *   onOpenChange  — setter
 *   defaultRole   — "primary" | "supporting"
 *   onCreated     — callback(goal) after AI-approved goal is saved
 *   skipOption    — show "Skip for now"
 *   onSkip        — callback when user skips
 */
export default function StrategicGoalWizard({ open, onOpenChange, defaultRole = "primary", onCreated, skipOption = false, onSkip, hideRole = false, hidePriority = false }) {
  const [step, setStep] = useState("form"); // form -> preview -> saving -> done
  const [draft, setDraft] = useState(() => ({ ...emptyDraft(), strategic_role: defaultRole }));
  const [preview, setPreview] = useState(null); // {stages:[]}
  const [busy, setBusy] = useState(false);

  const upd = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  const close = () => { onOpenChange(false); setTimeout(() => { setStep("form"); setDraft({ ...emptyDraft(), strategic_role: defaultRole }); setPreview(null); }, 400); };

  const generate = async () => {
    if (!draft.name.trim()) { toast.error("Give your goal a name."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/goals/strategic-roadmap", draft);
      setPreview(data.preview);
      setStep("preview");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI could not draft the roadmap. Try again.");
    } finally { setBusy(false); }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/goals/strategic-roadmap", draft);
      setPreview(data.preview);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not regenerate.");
    } finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/goals/strategic-approve", {
        draft, stages: preview?.stages || [],
      });
      setStep("done");
      onCreated && onCreated(data.goal);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save this Strategic Goal.");
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4" data-testid="strategic-wizard">
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl bg-card p-5 sm:rounded-3xl sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-brand-teal/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-brand-teal">
              <Compass className="h-3 w-3" /> Business Direction
            </div>
            <h2 className="mt-2 font-display text-xl font-semibold tracking-tight">
              {step === "form" && "Set your Strategic Goal"}
              {step === "preview" && "Your AI-drafted roadmap"}
              {step === "done" && "Direction is locked in."}
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {step === "form" && "This shapes your Command Center, briefing and every AI recommendation."}
              {step === "preview" && "Review, edit or regenerate. You can also skip and add later."}
            </p>
          </div>
          <button onClick={close} className="text-muted-foreground hover:text-foreground" data-testid="strategic-wizard-close"><X className="h-5 w-5" /></button>
        </div>

        {step === "form" && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-xs font-medium text-muted-foreground sm:col-span-2">
                Goal name
                <input value={draft.name} onChange={upd("name")} placeholder="e.g. Grow MRR from $2k to $20k" className={`${inputCls} mt-1`} data-testid="strategic-goal-name" />
              </label>
              {!hideRole && (
                <label className="block text-xs font-medium text-muted-foreground">
                  Type
                  <select value={draft.strategic_role} onChange={upd("strategic_role")} className={`${inputCls} mt-1`} data-testid="strategic-goal-role">
                    <option value="primary">Primary (1 per workspace)</option>
                    <option value="supporting">Supporting (up to 2)</option>
                  </select>
                </label>
              )}
              <label className="block text-xs font-medium text-muted-foreground">
                Category
                <select value={draft.category} onChange={upd("category")} className={`${inputCls} mt-1`} data-testid="strategic-goal-category">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="block text-xs font-medium text-muted-foreground">
                <span className="flex items-center gap-1">
                  Current position <span className="text-[10px] text-muted-foreground">(optional)</span>
                </span>
                <input value={draft.baseline} onChange={upd("baseline")} placeholder="e.g. 12 customers today, ₹50,000 MRR today, 18% conversion rate today" className={`${inputCls} mt-1`} data-testid="strategic-goal-baseline" />
                <span className="mt-0.5 block text-[10px] text-muted-foreground">Where are you starting from? Helps AI measure progress and create a realistic roadmap.</span>
              </label>
              <label className="block text-xs font-medium text-muted-foreground">
                Target
                <input value={draft.target} onChange={upd("target")} placeholder="e.g. 20000" className={`${inputCls} mt-1`} data-testid="strategic-goal-target" />
              </label>
              <label className="block text-xs font-medium text-muted-foreground">
                Unit
                <input value={draft.target_unit} onChange={upd("target_unit")} placeholder="MRR / customers / %" className={`${inputCls} mt-1`} data-testid="strategic-goal-unit" />
              </label>
              <label className="block text-xs font-medium text-muted-foreground">
                Target date
                <input type="date" value={draft.target_date || ""} onChange={upd("target_date")} className={`${inputCls} mt-1`} data-testid="strategic-goal-date" />
              </label>
              {!hidePriority && (
                <label className="block text-xs font-medium text-muted-foreground">
                  Priority
                  <select value={draft.priority} onChange={upd("priority")} className={`${inputCls} mt-1`} data-testid="strategic-goal-priority">
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </label>
              )}
              <label className="block text-xs font-medium text-muted-foreground sm:col-span-2">
                Why it matters
                <textarea rows={2} value={draft.why_matters} onChange={upd("why_matters")} className={`${inputCls} mt-1 resize-none`} data-testid="strategic-goal-why" placeholder="What does hitting this unlock for the business or your life?" />
              </label>
              <label className="block text-xs font-medium text-muted-foreground sm:col-span-2">
                Biggest obstacle
                <textarea rows={2} value={draft.biggest_obstacle} onChange={upd("biggest_obstacle")} className={`${inputCls} mt-1 resize-none`} data-testid="strategic-goal-obstacle" placeholder="What's most likely to stop you?" />
              </label>
            </div>
            <div className="flex flex-wrap justify-between gap-2 pt-2">
              {skipOption && (
                <button onClick={() => { onSkip && onSkip(); close(); }} data-testid="strategic-wizard-skip" className={outline}>
                  Skip for now
                </button>
              )}
              <div className="ml-auto">
                <button onClick={generate} disabled={busy || !draft.name.trim()} className={primary} data-testid="strategic-wizard-generate">
                  <Sparkles className="h-4 w-4" /> {busy ? "Thinking…" : "Draft roadmap with AI"}
                </button>
              </div>
            </div>
          </div>
        )}

        {step === "preview" && (
          <div className="space-y-3">
            <div className="rounded-xl border border-border bg-background p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{draft.strategic_role} · {draft.category}</p>
              <p className="mt-1 font-display text-base font-semibold">{draft.name}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {draft.baseline && <>from <strong>{draft.baseline}</strong> </>}
                {draft.target && <>→ <strong>{draft.target} {draft.target_unit}</strong> </>}
                {draft.target_date && <>by <strong>{draft.target_date}</strong></>}
              </p>
            </div>
            <div className="space-y-2" data-testid="strategic-preview">
              {(preview?.stages || []).length === 0 && (
                <p className="rounded-xl bg-muted/40 p-3 text-xs text-muted-foreground">The AI didn't return a structured plan. Try Regenerate with more detail.</p>
              )}
              {(preview?.stages || []).map((s, i) => (
                <div key={s.temp_id || i} className="rounded-xl border border-border bg-background p-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="text-sm font-semibold">Stage {i + 1}: {s.name}</p>
                  </div>
                  {s.description && <p className="mt-0.5 text-xs text-muted-foreground">{s.description}</p>}
                  <ul className="mt-2 space-y-1">
                    {(s.milestones || []).map((m, j) => (
                      <li key={m.temp_id || j} className="flex items-start gap-2 text-xs">
                        <span className="mt-1.5 h-1 w-1 rounded-full bg-brand-teal" />
                        <span className="flex-1"><span className="font-medium">{m.name}</span>{m.target_metric && <span className="ml-1 rounded bg-muted px-1 text-[10px] text-muted-foreground">{m.target_metric}</span>}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <button onClick={() => setStep("form")} className={outline} data-testid="strategic-wizard-back-form"><ArrowLeft className="h-3 w-3" /> Edit details</button>
              <button onClick={regenerate} disabled={busy} className={outline} data-testid="strategic-wizard-regenerate"><RefreshCw className="h-3 w-3" /> {busy ? "…" : "Regenerate"}</button>
              <button onClick={approve} disabled={busy || !(preview?.stages || []).length} className={`${primary} ml-auto`} data-testid="strategic-wizard-approve">
                {busy ? "Saving…" : "Approve & lock in"} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {step === "done" && (
          <div className="mt-4 text-center" data-testid="strategic-wizard-done">
            <CheckCircle2 className="mx-auto h-12 w-12 text-brand-teal" />
            <p className="mt-3 font-display text-lg font-semibold">Direction locked in.</p>
            <p className="mt-1 text-sm text-muted-foreground">Your Strategic Goal now anchors the Command Center, briefing and every AI recommendation.</p>
            <button onClick={close} className={`${primary} mt-4`} data-testid="strategic-wizard-done-close">Continue</button>
          </div>
        )}
      </div>
    </div>
  );
}
